import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
import { prefersReducedMotion } from '@theme/utilities';

if (!customElements.get('load-image')) {
  customElements.define(
    'load-image',
    class LoadImage extends Component {
      /** @type {MutationObserver | null} */
      #observer = null;

      connectedCallback() {
        this.#setupImages();
        this.#observeImages();
      }

      disconnectedCallback() {
        if (this.#observer) {
          this.#observer.disconnect();
        }
      }

      #setupImages() {
        const images = this.querySelectorAll('img');

        for (const img of images) {
          if (img.complete) {
            img.toggleAttribute('data-loaded', true);

            continue;
          }

          img.toggleAttribute('data-loading', true);

          img.addEventListener(
            'load',
            () => {
              img.toggleAttribute('data-loading', false);
              img.toggleAttribute('data-loaded', true);
            },
            { once: true },
          );

          img.addEventListener(
            'error',
            () => {
              img.toggleAttribute('data-loading', false);
              img.toggleAttribute('data-loaded', true);
            },
            { once: true },
          );
        }
      }

      #observeImages() {
        this.#observer = new MutationObserver(() => {
          this.#setupImages();
        });

        if (this.#observer) {
          this.#observer.observe(this, {
            childList: true,
            subtree: true,
          });
        }
      }
    },
  );
}

/**
 * A deferred media element
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  /** @type {IntersectionObserver | null} */
  #autoplayObserver = null;

  /**
   * Set when the visitor pauses through the pause control, so scrolling away and back doesn't
   * resume a video they deliberately stopped. Cleared when they press play again.
   * @type {boolean}
   */
  #userPaused = false;

  /** @type {IntersectionObserver | null} */
  #prefetchObserver = null;

  /** @type {string | null} */
  #renditionSrc = null;

  /**
   * Whether the visitor can currently see the media: false once the autoplay observer reports it off
   * screen, true again when it comes back, and true for an explicit play, which only happens on screen.
   * Elements without autoplay are never observed, so it stays at its default for them.
   * @type {boolean}
   */
  #onScreen = true;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Reduced motion: start paused rather than not autoplaying at all — without a pause control
    // there'd be no way left to start the video. Treated as a visitor-initiated pause.
    if (this.hasAttribute('autoplay') && prefersReducedMotion() && this.querySelector('[ref="toggleMediaButton"]')) {
      this.#userPaused = true;
    }

    // For autoplay videos, defer loading & playback until the element
    // actually intersects the viewport. This prevents off-screen carousel
    // slides from downloading & autoplaying videos that the visitor may
    // never see. When the element scrolls back out of view we pause it,
    // and resume (or restart, if it ended) when it re-enters.
    if (this.hasAttribute('autoplay')) {
      this.#autoplayObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            this.#onScreen = entry.isIntersecting;

            if (entry.isIntersecting) this.#loadAndSettlePlayback();
            else this.pauseMedia();
          }
        },
        { threshold: 0.25 },
      );
      this.#autoplayObserver.observe(this);
    } else if (this.#playlistSrc) {
      // Click-to-play can't wait on the network at click time (Safari drops the gesture grant across an
      // await), so resolve the rendition while the video is still approaching the viewport. The master
      // playlist is a few hundred bytes and lands long before a visitor can scroll to the play button.
      this.#prefetchObserver = new IntersectionObserver(
        async (entries, observer) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          await this.#resolveRendition();
          // Keep observing after a timed-out or zero-width attempt so a later pass can still downgrade;
          // scrolling back and forth costs at most a repeat of one CDN-cached playlist request.
          if (this.#renditionSrc) observer.disconnect();
        },
        { rootMargin: '400px' },
      );
      this.#prefetchObserver.observe(this);
    }

    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();

    if (this.#autoplayObserver) {
      this.#autoplayObserver.disconnect();
      this.#autoplayObserver = null;
    }

    if (this.#prefetchObserver) {
      this.#prefetchObserver.disconnect();
      this.#prefetchObserver = null;
    }
  }

  /**
   * @param {boolean} isPlaying
   */
  updatePlayPause(isPlaying) {
    this.isPlaying = isPlaying;

    const toggleMediaButton = this.refs.toggleMediaButton;

    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);

      // The accessible name carries the state; no aria-pressed, or a screen reader announces it twice.
      const label = isPlaying ? toggleMediaButton.dataset.labelPause : toggleMediaButton.dataset.labelPlay;
      if (label) toggleMediaButton.setAttribute('aria-label', label);
    }

    this.toggleAttribute('playing', this.isPlaying);
  }

  /**
   * Loads the media, then applies the playback state it should end up in.
   *
   * loadContent may await the rendition playlist, so the <video> can land a network round-trip after the
   * trigger: playing before it is appended no-ops on the media while still flipping the button to
   * "playing", and a visitor who scrolled past in the meantime would get an element that autoplays itself
   * off screen with no further observer callback coming.
   *
   * Not for a visitor's first click on a click-to-play video — that has to reach play() inside the click's
   * own task or Safari drops the gesture grant. See showDeferredMedia and loadContent.
   * @returns {Promise<void>}
   */
  async #loadAndSettlePlayback() {
    await this.loadContent(false);

    if (this.#onScreen && !this.#userPaused) this.playMedia();
    else this.pauseMedia();
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.updatePlayPause(true);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  async loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    // Set before the await below so a second intersection can't start a parallel load.
    this.setAttribute('data-media-loaded', 'true');

    if (content instanceof HTMLVideoElement) {
      // Only muted autoplay clips may wait here: their poster covers the gap and no user gesture is at
      // stake. Click-to-play must reach play() in the same task as the click, or Safari drops the gesture
      // grant across the await, so it uses only what the prefetch observer already resolved.
      if (this.hasAttribute('autoplay')) await this.#resolveRendition();

      if (this.#renditionSrc) {
        const source = content.querySelector('source');
        if (source) source.src = this.#renditionSrc;
      }
    }

    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    if (this.#userPaused) {
      // Both sources start themselves: <video> through its autoplay attribute, an embed through the
      // autoplay flag already in its src. The embed only accepts commands once its player has loaded.
      if (content instanceof HTMLVideoElement) content.removeAttribute('autoplay');
      else content.addEventListener('load', () => this.pauseMedia(), { once: true });

      this.pauseMedia();
    } else if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    }
  }

  /** @returns {string | undefined} The HLS master playlist of the templated video, before it is cloned. */
  get #playlistSrc() {
    return this.querySelector('template')?.content.querySelector('video')?.dataset.hlsSrc;
  }

  /**
   * Remembers the smallest Shopify transcode that still covers this element's rendered width.
   * The 1080p MP4 that Liquid hands us runs up to 7.2 Mbps (~13 MB for an 11s clip) and the gallery
   * only ever shows it at ~380px (mobile) or ~626px (desktop), so the top transcode is pure waste.
   * Liquid exposes no smaller MP4, but the HLS master playlist lists every rendition and its
   * progressive twin sits at a derivable URL. On any miss we keep the 1080p MP4 already in markup.
   * @returns {Promise<void>}
   */
  async #resolveRendition() {
    const playlistSrc = this.#playlistSrc;
    const slotWidth = this.clientWidth;
    if (!playlistSrc || !slotWidth) return;

    // Autoplay clips are decorative, muted and looping, so a slight upscale is invisible: 0.85 lets a
    // 720p rendition serve a 626px slot. Click-to-play means the visitor chose to watch, so hold a
    // higher floor there. ponytail: two constants, no per-block setting until someone asks for one.
    const target = slotWidth * (this.hasAttribute('autoplay') ? 0.85 : 1.2);

    try {
      const playlistUrl = new URL(playlistSrc, location.href);
      // A stalled playlist request must never keep the <video> out of the DOM; the markup MP4 is fine.
      const response = await fetch(playlistUrl, { signal: AbortSignal.timeout(1000) });
      if (!response.ok) return;
      const playlist = await response.text();

      const renditions = [...playlist.matchAll(/RESOLUTION=(\d+)x\d+[^\n]*\n\s*(\S+\.m3u8)/g)]
        .map(([, width, name]) => ({
          width: Number(width),
          // e.g. `….HD-720p.4.5Mbps.hls.86190156_.m3u8` -> `….HD-720p-4.5Mbps-86190156.mp4`
          url: new URL(name, playlistUrl).href.replace(
            /\.(HD|SD)-(\d+p)\.([\d.]+Mbps)\.hls\.(\d+)_\.m3u8$/,
            '.$1-$2-$3-$4.mp4',
          ),
        }))
        .filter((rendition) => rendition.url.endsWith('.mp4'))
        .sort((a, b) => a.width - b.width);

      this.#renditionSrc = renditions.find((rendition) => rendition.width >= target)?.url ?? null;
    } catch {
      // Network or parse failure: the markup already carries a playable source.
    }
  }

  toggleMedia() {
    this.#userPaused = this.isPlaying;

    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      // The pause control only exists on autoplay videos, so an explicit play means the visitor is
      // looking at it — even if less than the observer's 25% is in view.
      this.#onScreen = true;
      this.#loadAndSettlePlayback();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*',
      );
    } else {
      this.querySelector('video')?.play();
    }

    this.updatePlayPause(true);
  }

  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*',
      );
    } else {
      this.querySelector('video')?.pause();
    }

    this.updatePlayPause(false);
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent(focus = true) {
    const loaded = super.loadContent(focus);

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);

    return loaded;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal },
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal },
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
